import { useIntl } from "react-intl";
import { SimpleFormSelect, SimpleFormSelectOption } from "../form/SimpleFormSelect";
import { SimpleFormTextarea } from "../form/SimpleFormTextarea";
import { Col, Row } from "react-bootstrap";
import { useField } from "formik";
import { useEffect, useState } from "react";
import moment from "moment";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "src/app/utils/AssetHelpers";
import { Frequency } from "src/app/types/CreateOrders";
import { getKeywordFromString } from "src/app/utils/keywordsHelper";

export interface ICreateOrdersForm {
    moneySiteId: { options: SimpleFormSelectOption[]; loading: boolean };
    thematicId: { options: SimpleFormSelectOption[]; loading: boolean };
    frequency: { options: SimpleFormSelectOption[]; loading?: boolean };
    writingType: { options: SimpleFormSelectOption[]; loading?: boolean };
    wordCount: { options: SimpleFormSelectOption[]; loading?: boolean };
    hasMoneySiteId?: boolean;
    isForRankOrders?: boolean;
}
const CreateOrdersForm = (props: ICreateOrdersForm) => {
    const {
        moneySiteId = { options: [], loading: false },
        thematicId = { options: [], loading: false },
        frequency = { options: [] },
        writingType = { options: [] },
        wordCount = { options: [] },
        isForRankOrders = false,
        hasMoneySiteId = true,
    } = props;
    const { formatMessage } = useIntl();
    const [days, setDays] = useState<string>();
    const [keywords] = useField("keywords");
    const [frequencyInput] = useField("frequency");
    const initialDate = moment().add(2, "d");
    const arrayOfKeywords = getKeywordFromString(keywords.value || "");

    const [writingTypeField] = useField("writingType");
    const selectedWritingType = writingTypeField.value;
    const price =
        selectedWritingType === "AI_AUTO"
            ? process.env.REACT_APP_KEYWORDS_AUTO_AI_PRICE || "0"
            : process.env.REACT_APP_KEYWORDS_MANUAL_AI_PRICE || "0";

    useEffect(() => {
        if (frequencyInput.value === undefined || keywords.value === undefined) {
            if (days !== undefined) {
                setDays(undefined);
            }
            return;
        }
        let duration = 0;
        switch (frequencyInput.value) {
            case Frequency.EVERYDAY:
                duration = arrayOfKeywords.length - 1;
                setDays(initialDate.add(duration, "d").format("DD/MM/YYYY"));
                break;
            case Frequency.EVERY_2_DAYS:
                duration = (arrayOfKeywords.length - 1) * 2;
                setDays(initialDate.add(duration, "d").format("DD/MM/YYYY"));
                break;
            case Frequency.EVERY_3_DAYS:
                duration = (arrayOfKeywords.length - 1) * 3;
                setDays(initialDate.add(duration, "d").format("DD/MM/YYYY"));
                break;
            case Frequency.ONE_BY_WEEK:
                duration = arrayOfKeywords.length - 1;
                setDays(initialDate.add(duration, "w").format("DD/MM/YYYY"));
                break;
        }
    }, [keywords.value, frequencyInput.value]);

    return (
        <Row>
            {isForRankOrders && (
                <div className="mb-2 guidelines">
                    <SVG
                        className="pulse-animation"
                        src={toAbsoluteUrl("/media/icons/duotune/general/gen044.svg")}
                    />
                    {formatMessage({ id: "common.entity.guidelinesRanking" })}
                </div>
            )}
            <Col className="">
                <div className="d-flex flex-column justify-content-between w-100 h-100">
                    <div className="w-100 d-flex flex-column">
                        {hasMoneySiteId && (
                            <SimpleFormSelect
                                isClearable={false}
                                name="moneySiteId"
                                isLoading={moneySiteId.loading}
                                label={formatMessage({ id: "common.field.moneySite" })}
                                values={moneySiteId.options}
                            />
                        )}
                        {!hasMoneySiteId && (
                            <SimpleFormSelect
                                isClearable={false}
                                name="thematicId"
                                isLoading={thematicId.loading}
                                label={formatMessage({ id: "common.field.thematic" })}
                                values={thematicId.options}
                            />
                        )}
                        <SimpleFormSelect
                            isClearable={false}
                            name="wordCount"
                            label={formatMessage({ id: "common.field.wordsCount" })}
                            values={wordCount.options}
                        />
                        <SimpleFormSelect
                            isClearable={false}
                            name="frequency"
                            label={formatMessage({ id: "common.field.frequency" })}
                            values={frequency.options}
                        />
                        {days &&
                            (arrayOfKeywords.length > 1 ? (
                                <span>
                                    {formatMessage(
                                        { id: "common.field.frequency.betweenDates" },
                                        {
                                            inicalDate: initialDate.format("DD/MM/YYYY"),
                                            lastDate: days,
                                        }
                                    )}
                                </span>
                            ) : (
                                <span>
                                    {formatMessage(
                                        { id: "common.field.frequency.publishedOn" },
                                        { date: initialDate.format("DD/MM/YYYY") }
                                    )}
                                </span>
                            ))}

                        <SimpleFormSelect
                            name="writingType"
                            isClearable={false}
                            label={formatMessage({ id: "common.field.writingType" })}
                            values={writingType.options}
                        />
                    </div>
                    <div className="d-flex justify-content-end mt-2 p-2 bg-light rounded">
                        <div className="selected-keywords-counter">
                            <strong>{formatMessage({ id: "common.text.totalKeywords" })} : </strong>
                            <span className="badge bg-primary me-2">{arrayOfKeywords.length}</span>
                            <div className="d-flex flex-column text-end">
                                <div className="d-flex flex-column text-end">
                                    <strong>
                                        {formatMessage({
                                            id: "common.text.totalprice",
                                        })}{" "}
                                        :{" "}
                                        {new Intl.NumberFormat("de-DE", {
                                            style: "currency",
                                            currency: "EUR",
                                        }).format(arrayOfKeywords.length * parseFloat(price))}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Col>
            <Col>
                <SimpleFormTextarea
                    name="keywords"
                    rows={20}
                    label={formatMessage({ id: "common.field.keywords" })}
                />
            </Col>
        </Row>
    );
};

export default CreateOrdersForm;
